import axios from 'axios';
import store from '@/store';

/**
 * Service de gestion de la grille de cartes (articles, publicités, widgets)
 * Gère la distribution et le placement des différents types de cartes
 * dans les grilles d'articles affichées sur le site
 * 
 * Ce service communique avec l'API backend pour obtenir les grilles optimisées
 * et gère le cache client pour une meilleure performance.
 */
class GridService {
  constructor() {
    // Configuration par défaut
    this.config = {
      // Fréquence des publicités (1 sur X cartes)
      adFrequency: 8,
      
      // Fréquence des widgets météo (1 sur X lignes, 0 pour désactiver)
      weatherWidgetFrequency: 2,
      
      // Nombre d'articles par page pour le chargement initial
      initialLoad: 24,
      
      // Taille des pages pour le chargement infini
      pageSize: 12,
      
      // Configuration du layout
      layout: {
        recommended: 0.4,  // 40% articles recommandés 
        trending: 0.3,     // 30% articles tendance
        latest: 0.3,       // 30% articles récents
      },
      
      // Nombre d'articles par ligne selon les breakpoints
      itemsPerRow: {
        xs: 1,  // <= 600px (mobile)
        sm: 2,  // <= 960px (tablette portrait)
        md: 3,  // <= 1264px (tablette paysage)
        lg: 4,  // <= 1904px (desktop)
        xl: 4   // > 1904px (grand écran)
      },
    };
    
    // Cache local des grilles
    this.cache = {
      standard: null,
      trending: null,
      mySources: null,
      categories: {}
    };
  }

  /**
   * Récupère une grille d'articles depuis l'API
   * @param {Object} options - Options de la grille
   * @returns {Promise<Array>} Grille d'articles et publicités
   */
  async fetchGrid(options = {}) {
    const {
      mode = 'standard',
      categories = [],
      keywords = [],
      gridSize = this.config.initialLoad,
      page = 1,
      useCachedData = true
    } = options;
    
    // Si on a des données en cache et qu'on autorise l'utilisation du cache
    if (useCachedData && this.hasCache(mode, categories)) {
      return this.getFromCache(mode, categories);
    }
    
    try {
      // Préparer les paramètres de requête
      const queryParams = new URLSearchParams();
      
      // Ajouter les paramètres de base
      queryParams.append('mode', mode);
      queryParams.append('size', gridSize.toString());
      queryParams.append('page', page.toString());
      queryParams.append('adFrequency', this.config.adFrequency.toString());
      
      // Ajouter les ratios de layout
      queryParams.append('recommendedRatio', this.config.layout.recommended.toString());
      queryParams.append('trendingRatio', this.config.layout.trending.toString());
      queryParams.append('latestRatio', this.config.layout.latest.toString());
      
      // Ajouter les catégories si spécifiées
      if (categories && categories.length > 0) {
        queryParams.append('categories', categories.join(','));
      }
      
      // Ajouter les mots-clés si spécifiés
      if (keywords && keywords.length > 0) {
        queryParams.append('keywords', keywords.join(','));
      }
      
      // Faire la requête API
      const response = await axios.get(`/api/grid?${queryParams.toString()}`);
      
      // Mettre en cache la réponse
      const gridData = response.data.data.grid;
      this.setCache(mode, categories, gridData);
      
      return gridData;
    } catch (error) {
      console.error('Erreur lors de la récupération de la grille:', error);
      
      // En cas d'erreur, retourner une grille vide
      return [];
    }
  }
  
  /**
   * Récupère une grille avec pagination (pour le chargement infini)
   * @param {Object} options - Options de pagination
   * @returns {Promise<Object>} Réponse avec les nouveaux éléments et les infos de pagination
   */
  async fetchMoreItems(options = {}) {
    const {
      mode = 'standard',
      page = 2,
      categories = [],
      keywords = [],
      size = this.config.pageSize
    } = options;
    
    try {
      // Préparer les paramètres de requête
      const queryParams = new URLSearchParams();
      
      // Ajouter les paramètres de base
      queryParams.append('mode', mode);
      queryParams.append('size', size.toString());
      queryParams.append('page', page.toString());
      queryParams.append('adFrequency', this.config.adFrequency.toString());
      
      // Ajouter les catégories si spécifiées
      if (categories && categories.length > 0) {
        queryParams.append('categories', categories.join(','));
      }
      
      // Ajouter les mots-clés si spécifiés
      if (keywords && keywords.length > 0) {
        queryParams.append('keywords', keywords.join(','));
      }
      
      // Faire la requête API
      const response = await axios.get(`/api/grid?${queryParams.toString()}`);
      
      // Retourner les données
      return {
        items: response.data.data.grid,
        pagination: response.data.pagination || null,
        hasMore: response.data.pagination ? 
          response.data.pagination.hasNextPage : false
      };
    } catch (error) {
      console.error('Erreur lors du chargement de plus d\'articles:', error);
      
      // En cas d'erreur, retourner une réponse vide
      return {
        items: [],
        pagination: null,
        hasMore: false
      };
    }
  }
  
  /**
   * Récupère une grille dimensionnée spécifiquement (colonnes x lignes)
   * @param {Object} options - Options de dimensions
   * @returns {Promise<Object>} Grille dimensionnée
   */
  async fetchDimensionedGrid(options = {}) {
    const {
      columns = 4,
      rows = 6,
      categories = [],
      keywords = []
    } = options;
    
    try {
      // Préparer les paramètres de requête
      const queryParams = new URLSearchParams();
      
      // Ajouter les dimensions
      queryParams.append('columns', columns.toString());
      queryParams.append('rows', rows.toString());
      
      // Ajouter les catégories si spécifiées
      if (categories && categories.length > 0) {
        queryParams.append('categories', categories.join(','));
      }
      
      // Ajouter les mots-clés si spécifiés
      if (keywords && keywords.length > 0) {
        queryParams.append('keywords', keywords.join(','));
      }
      
      // Faire la requête API
      const response = await axios.get(`/api/grid/dimensions?${queryParams.toString()}`);
      
      return response.data.data.grid;
    } catch (error) {
      console.error('Erreur lors de la récupération de la grille dimensionnée:', error);
      
      // En cas d'erreur, retourner une grille vide
      return {
        columns,
        rows,
        items: []
      };
    }
  }
  
  /**
   * Enregistre une interaction avec un élément de la grille
   * @param {Object} data - Données de l'interaction
   * @returns {Promise<boolean>} Indicateur de succès
   */
  async recordInteraction(data) {
    try {
      await axios.post('/api/grid/interaction', data);
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'interaction:', error);
      return false;
    }
  }
  
  /**
   * Met à jour les préférences de grille de l'utilisateur
   * @param {Object} preferences - Nouvelles préférences
   * @returns {Promise<boolean>} Indicateur de succès
   */
  async updatePreferences(preferences) {
    try {
      // Vérifier si l'utilisateur est authentifié
      if (!store.getters['auth/isAuthenticated']) {
        console.warn('Impossible de mettre à jour les préférences: utilisateur non authentifié');
        return false;
      }
      
      await axios.post('/api/grid/preferences', preferences);
      
      // Vider le cache après la mise à jour des préférences
      this.clearCache();
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour des préférences:', error);
      return false;
    }
  }
  
  /**
   * Prépare les données de la grille pour les composants frontend
   * Convertit les données brutes de l'API en structure compatible avec nos composants
   * @param {Array} gridData - Données brutes de la grille provenant de l'API
   * @returns {Array} Données formatées pour les composants
   */
  prepareGridForComponents(gridData) {
    if (!gridData || !Array.isArray(gridData)) return [];
    
    // Ajouter des clés uniques et autres propriétés nécessaires
    return gridData.map((item, index) => {
      // Pour garantir un ID unique même pour les éléments sans ID
      const uniqueId = item.id || item._id || `item-${Date.now()}-${index}`;
      
      return {
        ...item,
        id: uniqueId,
        position: index
      };
    });
  }
  
  /**
   * Récupère le nombre d'éléments par ligne selon la taille d'écran actuelle
   * @returns {Number} Nombre d'éléments par ligne
   */
  getItemsPerRow() {
    // Par défaut, utiliser la configuration pour desktop
    let itemsPerRow = this.config.itemsPerRow.lg;
    
    // Si dans un navigateur, détecter la taille d'écran actuelle
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      
      if (width <= 600) {
        itemsPerRow = this.config.itemsPerRow.xs;
      } else if (width <= 960) {
        itemsPerRow = this.config.itemsPerRow.sm;
      } else if (width <= 1264) {
        itemsPerRow = this.config.itemsPerRow.md;
      } else if (width <= 1904) {
        itemsPerRow = this.config.itemsPerRow.lg;
      } else {
        itemsPerRow = this.config.itemsPerRow.xl;
      }
    }
    
    return itemsPerRow;
  }
  
  /**
   * Vérifie si une grille est en cache
   * @param {String} mode - Mode de la grille
   * @param {Array} categories - Catégories (si applicable)
   * @returns {Boolean} true si la grille est en cache
   */
  hasCache(mode, categories = []) {
    if (mode === 'category' && categories.length > 0) {
      return Boolean(this.cache.categories[categories[0]]);
    }
    
    return Boolean(this.cache[mode]);
  }
  
  /**
   * Récupère une grille depuis le cache
   * @param {String} mode - Mode de la grille
   * @param {Array} categories - Catégories (si applicable)
   * @returns {Array} Grille mise en cache
   */
  getFromCache(mode, categories = []) {
    if (mode === 'category' && categories.length > 0) {
      return this.cache.categories[categories[0]] || [];
    }
    
    return this.cache[mode] || [];
  }
  
  /**
   * Met une grille en cache
   * @param {String} mode - Mode de la grille
   * @param {Array} categories - Catégories (si applicable)
   * @param {Array} data - Données de la grille
   */
  setCache(mode, categories = [], data) {
    if (mode === 'category' && categories.length > 0) {
      this.cache.categories[categories[0]] = data;
    } else {
      this.cache[mode] = data;
    }
  }
  
  /**
   * Vide le cache des grilles
   */
  clearCache() {
    this.cache = {
      standard: null,
      trending: null,
      mySources: null,
      categories: {}
    };
  }
}

// Exporter une instance unique du service
export default new GridService();